import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle, LucideIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";

interface SimplifiedRoleCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  hasAccess: boolean;
  accessAction?: {
    label: string;
    path?: string;
    onClick?: () => void;
  };
  features?: string[];
  roleInfo?: string;
  companyDetails?: string[];
  alwaysShowAction?: boolean;
}

export function SimplifiedRoleCard({
  icon: Icon,
  title,
  description,
  hasAccess,
  accessAction,
  features,
  roleInfo,
  companyDetails,
  alwaysShowAction = false
}: SimplifiedRoleCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('workspace');

  return (
    <div className={`p-6 rounded-lg transition-colors ${
      hasAccess 
        ? 'bg-primary/5 dark:bg-primary/10' 
        : 'bg-card hover:bg-muted/50'
    }`}>
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
          hasAccess ? 'bg-primary/10 dark:bg-primary/20' : 'bg-muted'
        }`}>
          <Icon className={`w-6 h-6 ${
            hasAccess ? 'text-primary' : 'text-muted-foreground'
          }`} />
        </div>
        
        <div className="flex-1 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-lg text-foreground">{title}</h3>
                {hasAccess && (
                  <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400" />
                )}
              </div>
              <p className="text-muted-foreground leading-relaxed">{description}</p>
            </div>
            
            <div className="flex flex-col items-end gap-2">
              {hasAccess ? (
                <Badge variant="default" className="font-medium">
                  {roleInfo || t('role_active')}
                </Badge>
              ) : (
                accessAction && (
                  <Button 
                    onClick={() => {
                      if (accessAction.onClick) {
                        accessAction.onClick();
                      } else if (accessAction.path) {
                        navigate(accessAction.path);
                      }
                    }}
                    size="sm"
                    className="h-9 px-4"
                  >
                    {accessAction.label}
                  </Button>
                )
              )}
              
              {hasAccess && alwaysShowAction && accessAction && (
                <Button 
                  onClick={() => {
                    if (accessAction.onClick) {
                      accessAction.onClick();
                    } else if (accessAction.path) {
                      navigate(accessAction.path);
                    }
                  }}
                  size="sm"
                  variant="outline"
                  className="h-9 px-4"
                >
                  {accessAction.label}
                </Button>
              )}
            </div>
          </div>

          {features && (
            <div className="text-sm text-muted-foreground">
              <ul className="space-y-1">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-center">
                    <span className="w-1.5 h-1.5 bg-current rounded-full mr-3 opacity-60"></span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {companyDetails && companyDetails.length > 0 && (
            <div className="text-sm text-muted-foreground pt-3">
              <p className="font-medium mb-2 text-foreground/80">{t('your_companies')}:</p>
              <ul className="space-y-1">
                {companyDetails.map((companyName, index) => (
                  <li key={index} className="flex items-center">
                    <span className="w-1.5 h-1.5 bg-current rounded-full mr-3 opacity-60"></span>
                    {companyName}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}